import { ArrowUpCircle } from 'lucide-react';

import { AddonsAddonRecord } from '@api/types.gen';

import { Badge } from '@@/Badge';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';

interface Props {
  record?: AddonsAddonRecord;
}

/**
 * AddonVersionBadge shows the installed chart version and, when a newer version
 * is published, decorates it with an up-arrow — the version is the honest home
 * for the "update available" signal. It stays info/blue (amber is reserved for
 * the "Upgrade failed" status) and is never colour-only: the arrow icon carries
 * the meaning and the target version rides in the tooltip.
 */
export function AddonVersionBadge({ record }: Props) {
  if (!record?.chartVersion) {
    return null;
  }

  // Only decorate when there's a concrete newer version to point at, so the
  // arrow/tooltip never reference an undefined target.
  const latestVersion = record.availableVersions?.[0];
  const showUpdate = !!record.upgradeAvailable && !!latestVersion;

  const badge = (
    <Badge type="info" data-cy="addon-version-badge">
      v{record.chartVersion}
      {showUpdate && (
        <ArrowUpCircle
          className="h-3 w-3"
          data-cy="addon-update-available-indicator"
        />
      )}
    </Badge>
  );

  if (!showUpdate) {
    return badge;
  }

  return (
    <TooltipWithChildren message={`Version ${latestVersion} is available`}>
      <span>{badge}</span>
    </TooltipWithChildren>
  );
}
